import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Lesson Focus: The Power of Balance Over Perfection',
    content: [
      '<strong>Deep Dive:</strong> Your body is a high-performance machine, and nutrition is its fuel. Thinking of food as balance means understanding that occasional treats won\'t derail you, but consistent poor fuel will degrade your system. Poor diet doesn\'t just make you tired; it creates long-term inflammation, which is the root cause of conditions like heart disease and Type 2 diabetes. Good nutrition supports all biological processes: from immune cell production (defense) to tissue renewal (repair).'
    ],
    additionalSection: {
      title: 'Key Concepts:',
      content: [
        '<strong>Core Principle:</strong> Nutrition is essential for growth, repair, and energy production.',
        '<strong>The Payoff:</strong> "What you eat today becomes your strength tomorrow."',
      ]
    },
    additionalSection2: {
      title: 'Key Takeaway',
      content: [
        'The quality of your food directly dictates the quality of your daily life–your mood, your focus, and your energy.'
      ]
    }
  },
  {
    title: 'Activity: The Energy Audit',
    content: [
      '<strong>• The Point:</strong> This activity makes the abstract concept of nutrition immediately personal. By circling energizing foods (like eggs, fish, vegetables) and boxing sluggish foods (like heavy sweets or large fried portions), you identify your personal dietary patterns and their direct impact on your well-being.',
      '<strong>• Assessment Insight:</strong> Explaining "Food is fuel" means recognizing that carbohydrates (Go foods like rice and root crops) are gasoline, proteins (Grow foods like fish and meat) are engine oil, and vitamins/minerals (Glow foods like fruits and vegetables) are the coolant and spark plugs.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about nutrition fundamentals.',
        'The quiz will help you review key concepts about nutrition\'s role in your body, balanced eating, and understanding food as fuel.'
      ]
    }
  }
];

// Main component, matching the design of the standard assessment form
const NutritionalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM1');
  const totalSteps = moduleContent.length;
  
  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Only save completion status when finishing the last content step
      await saveProgress(nextStep, nextStep === totalSteps - 1);
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    // Don't change completion status when navigating back
    await saveProgress(prevStep, isCompleted);
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  // Only show completion message on the last step
  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding Nutrition & Your Body</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Learn how your body processes food, the essential nutrients you need, and how nutrition affects your overall health and wellbeing.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}


              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentStep === 0 ? handleExit : handleBack}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {currentStep < totalSteps - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-1/quiz')}
              >
                Take the Quiz
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule1Page;

